import React, { useState } from "react";
import Marquee from "../components/brand/Marquee";
import Nav from "../components/brand/Nav";
import Footer from "../components/brand/Footer";
import USMap from "../components/brand/USMap";
import StickerBadge from "../components/brand/StickerBadge";
import { MEDIA } from "../lib/media";
import { useScrollReveal } from "../lib/useScrollReveal";
import { toast } from "sonner";
import { createEmailSignup } from "../services/api";

const locations = [
  {
    id: "sf-polk",
    name: "POLK STREET",
    address: "1850 Polk St\nSan Francisco, CA",
    hours: "MON–FRI · 6A — 7P\nSAT–SUN · 7A — 6P",
    image: MEDIA.sf_storefront,
    open: true,
  },
];

const comingSoon = [
  { id: "nyc", city: "NEW YORK", state: "NY" },
  { id: "atx", city: "AUSTIN", state: "TX" },
  { id: "den", city: "DENVER", state: "CO" },
];

const pins = [{ x: 90, y: 200, id: "sf-polk", label: "SF" }];

const Locations = () => {
  const pageRef = useScrollReveal();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createEmailSignup(email);
      toast.success("You're on the list. We'll holler.");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 400) {
        toast.success("You're already on the list.");
        setEmail("");
      } else {
        toast.error("Something went sideways. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={pageRef}
      data-testid="page-locations"
      className="bg-bone text-navy"
    >
      <Marquee />
      <Nav />

      {/* HERO + MAP */}
      <section className="bg-navy text-bone pt-20 md:pt-28 pb-20 md:pb-32">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12">
          <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-red mb-6">
            ◢◢ THE NETWORK · USA
          </p>
          <h1
            data-testid="locations-headline"
            className="font-display uppercase leading-[0.82] tracking-tighter text-[14vw] md:text-[11vw] lg:text-[10vw]"
          >
            FIND A <span className="text-red">CAFÉ.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-bone/80 max-w-xl mt-6 leading-relaxed">
            One coast. More soon. The map is small. The mission isn't.
          </p>

          <div className="mt-10 md:mt-16">
            <USMap
              pins={pins}
              onPinClick={(p) => {
                const el = document.getElementById(p.id);
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            />
          </div>
        </div>
      </section>

      {/* LOCATION LIST */}
      <section className="bg-bone text-navy py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
          <div className="flex justify-between items-end mb-10 md:mb-14">
            <h2 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
              Open <span className="text-red">Now.</span>
            </h2>
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-navy/60">
              § DIRECTORY
            </p>
          </div>

          <div className="grid grid-cols-12 gap-8">
            {locations.map((loc) => (
              <article
                key={loc.id}
                id={loc.id}
                data-testid={`location-card-${loc.id}`}
                className="col-span-12 md:col-span-8 reveal"
              >
                <div className="relative grain">
                  <img
                    src={loc.image}
                    alt={loc.name}
                    className="w-full h-[260px] md:h-[460px] object-cover"
                  />
                  <div className="absolute -top-6 -right-4 md:-top-8 md:right-8">
                    <StickerBadge
                      color="red"
                      textColor="bone"
                      rotate={14}
                      size={120}
                    >
                      OPEN<br />NOW
                    </StickerBadge>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-7">
                    <h3 className="font-display uppercase leading-none tracking-tight text-5xl md:text-6xl">
                      {loc.name}
                    </h3>
                    <p className="font-mono whitespace-pre-line text-sm mt-4 leading-relaxed">
                      {loc.address}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-5 flex flex-col items-start md:items-end justify-between gap-4">
                    <p className="font-mono whitespace-pre-line text-xs md:text-sm uppercase tracking-[0.15em] text-navy/70 md:text-right">
                      {loc.hours}
                    </p>
                    <a
                      href="https://maps.google.com/?q=1850+Polk+St,+San+Francisco,+CA"
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`directions-link-${loc.id}`}
                      className="font-mono uppercase tracking-[0.2em] text-xs md:text-sm underline underline-offset-4 hover:text-red"
                    >
                      Get directions →
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON */}
      <section className="bg-navy text-bone py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
          <div className="flex justify-between items-end mb-10">
            <h2 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
              Coming to <span className="text-red">your city.</span>
            </h2>
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-bone/60">
              § HORIZON
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {comingSoon.map((c, i) => (
              <div
                key={c.id}
                className="col-span-12 md:col-span-4 border-4 border-bone/50 p-6 md:p-8 hover:border-red hover:-translate-y-1 transition-all"
                data-testid={`coming-soon-${c.id}`}
              >
                <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red mb-3">
                  ◢ NO. 0{i + 2}
                </p>
                <h3 className="font-display uppercase leading-none tracking-tight text-5xl md:text-6xl">
                  {c.city}
                </h3>
                <p className="font-mono uppercase tracking-[0.2em] text-xs mt-3 text-bone/70">
                  {c.state} · TBA
                </p>
              </div>
            ))}
          </div>

          {/* Notify form */}
          <form
            onSubmit={handleNotify}
            data-testid="notify-form"
            className="mt-14 md:mt-20 grid grid-cols-12 gap-4 items-end"
          >
            <div className="col-span-12 md:col-span-8">
              <label className="font-mono uppercase tracking-[0.2em] text-[10px] text-bone/60 block mb-2">
                Get notified when we land
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="notify-input"
                className="w-full bg-transparent border-4 border-bone rounded-none px-4 py-4 font-mono text-base text-bone placeholder:text-bone/40 brut-focus"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="notify-submit"
              className="col-span-12 md:col-span-4 bg-red text-bone border-4 border-bone hover:bg-bone hover:text-red transition-colors font-display uppercase tracking-wider text-xl md:text-2xl px-6 py-4 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Notify Me"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Locations;
